import type { ResumeData } from "@/types/resume";
import { Mail, Phone, MapPin, Globe, Linkedin, Github } from "lucide-react";
import { formatDate, getOrderedSections } from "./templateUtils";
import { bodyFont, headingFont } from "./useResumeFonts";

export function TemplateTwoColumn({ data, overridePrimary }: { data: ResumeData; overridePrimary?: string }) {
  const { personalInfo: p } = data;
  const PRIMARY = overridePrimary || "#1e293b";
  const FONT_BODY = bodyFont(data, "'Segoe UI', system-ui, sans-serif");
  const FONT_HEADING = headingFont(data, FONT_BODY);

  // Sidebar sections: contact, skills, languages, certifications
  // Main sections: summary, workExperience, education, projects, achievements
  const sidebarKeys = ["skills", "languages", "certifications"];
  const mainKeys = getOrderedSections(data).filter((k) => !sidebarKeys.includes(k));
  const orderedSidebar = getOrderedSections(data).filter((k) => sidebarKeys.includes(k));

  const mainRenderers: Record<string, React.ReactNode> = {
    summary: data.summary ? <MainSection color={PRIMARY} key="summary" title="Profile"><p>{data.summary}</p></MainSection> : null,
    workExperience: data.workExperience.length > 0 ? (
      <MainSection color={PRIMARY} key="workExperience" title="Experience">
        {data.workExperience.map((exp) => (
          <div key={exp.id} className="mb-3 last:mb-0">
            <strong>{exp.jobTitle}</strong>
            {exp.company && <span className="font-medium"> — {exp.company}</span>}
            <p className="text-[10px]" style={{ color: "#475569" }}>{exp.startDate && formatDate(exp.startDate)} — {exp.currentlyWorking ? "Present" : exp.endDate ? formatDate(exp.endDate) : ""}{exp.location ? ` | ${exp.location}` : ""}</p>
            {exp.responsibilities.filter(Boolean).length > 0 && <ul className="list-disc ml-4 mt-1 space-y-0.5">{exp.responsibilities.filter(Boolean).map((r, i) => <li key={i}>{r}</li>)}</ul>}
          </div>
        ))}
      </MainSection>
    ) : null,
    education: data.education.length > 0 ? (
      <MainSection color={PRIMARY} key="education" title="Education">
        {data.education.map((edu) => (
          <div key={edu.id} className="mb-2 last:mb-0">
            <strong>{edu.degree}</strong>{edu.institution && <span> — {edu.institution}</span>}
            {edu.graduationDate && <p className="text-[10px]" style={{ color: "#475569" }}>{formatDate(edu.graduationDate)}</p>}
            {edu.location && <p className="text-[10px]" style={{ color: "#475569" }}>{edu.location}</p>}
          </div>
        ))}
      </MainSection>
    ) : null,
    projects: data.projects.length > 0 ? (
      <MainSection color={PRIMARY} key="projects" title="Projects">
        {data.projects.map((proj) => (
          <div key={proj.id} className="mb-2 last:mb-0">
            <strong>{proj.title}</strong>{proj.role && <span> — {proj.role}</span>}
            {proj.description && <p className="mt-0.5">{proj.description}</p>}
          </div>
        ))}
      </MainSection>
    ) : null,
    achievements: data.achievements.filter(Boolean).length > 0 ? (
      <MainSection color={PRIMARY} key="achievements" title="Achievements">
        <ul className="list-disc ml-4 space-y-0.5">{data.achievements.filter(Boolean).map((a, i) => <li key={i}>{a}</li>)}</ul>
      </MainSection>
    ) : null,
  };

  const sidebarRenderers: Record<string, React.ReactNode> = {
    skills: data.skills.length > 0 ? (
      <SideSection key="skills" title="Skills">
        {data.skills.map((g) => (
          <div key={g.id} className="mb-2 last:mb-0">
            {g.category && <p className="font-bold text-[10px] mb-0.5" style={{ color: "#334155" }}>{g.category}</p>}
            <p className="text-[10px]">{g.skills.filter(Boolean).join(", ")}</p>
          </div>
        ))}
      </SideSection>
    ) : null,
    languages: data.languages.length > 0 ? (
      <SideSection key="languages" title="Languages">
        {data.languages.map((l, i) => <p key={i} className="text-[10px]">{l.language} — {l.proficiency}</p>)}
      </SideSection>
    ) : null,
    certifications: data.certifications.length > 0 ? (
      <SideSection key="certifications" title="Certifications">
        {data.certifications.map((c) => <p key={c.id} className="text-[10px] mb-1"><strong>{c.url ? <a href={c.url} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>{c.name}</a> : c.name}</strong>{c.issuer ? <br /> : null}{c.issuer}</p>)}
      </SideSection>
    ) : null,
  };

  return (
    <div className="text-xs leading-relaxed" style={{ fontFamily: FONT_BODY, color: "#1e293b", background: "#ffffff" }}>
      <div className="px-6 py-5 flex items-center gap-4" style={{ background: overridePrimary ? overridePrimary : "linear-gradient(135deg, #1e293b 0%, #334155 100%)", color: "#fff" }}>
        {p.photoUrl && <img src={p.photoUrl} alt="" width={64} height={64} loading="lazy" decoding="async" className="w-16 h-16 rounded-full object-cover shrink-0" style={{ border: "2px solid rgba(255,255,255,0.3)" }} />}
        <div>
          {p.fullName && <h1 className="text-xl font-bold mb-0.5" style={{ fontFamily: FONT_HEADING }}>{p.fullName}</h1>}
          {p.professionalTitle && <p className="text-sm opacity-80">{p.professionalTitle}</p>}
        </div>
      </div>
      <div className="flex">
        <div className="w-[40%] p-4 space-y-4" style={{ background: "#f1f5f9" }}>
          <SideSection title="Contact">
            <div className="space-y-1.5 text-[10px]">
              {p.email && <div className="flex items-center gap-1"><Mail className="w-2.5 h-2.5 shrink-0" /><span className="break-all">{p.email}</span></div>}
              {p.phone && <div className="flex items-center gap-1"><Phone className="w-2.5 h-2.5 shrink-0" />{p.phone}</div>}
              {p.city && <div className="flex items-center gap-1"><MapPin className="w-2.5 h-2.5 shrink-0" />{p.city}{p.country ? `, ${p.country}` : ""}</div>}
              {p.linkedin && <div className="flex items-center gap-1"><Linkedin className="w-2.5 h-2.5 shrink-0" /><span className="break-all">{p.linkedin}</span></div>}
              {p.github && <div className="flex items-center gap-1"><Github className="w-2.5 h-2.5 shrink-0" /><span className="break-all">{p.github}</span></div>}
              {p.portfolio && <div className="flex items-center gap-1"><Globe className="w-2.5 h-2.5 shrink-0" /><span className="break-all">{p.portfolio}</span></div>}
            </div>
          </SideSection>
          {orderedSidebar.map((key) => sidebarRenderers[key])}
        </div>
        <div className="flex-1 p-4 space-y-4">
          {mainKeys.map((key) => mainRenderers[key])}
        </div>
      </div>
    </div>
  );
}

function SideSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-[10px] font-bold uppercase tracking-wider mb-1.5 pb-1" style={{ color: "#334155", borderBottom: "1px solid #cbd5e1" }}>{title}</h2>
      {children}
    </div>
  );
}

function MainSection({ title, children, color = "#1e293b" }: { title: string; children: React.ReactNode; color?: string }) {
  return (
    <div>
      <h2 className="text-xs font-bold uppercase tracking-wider mb-1.5 pb-1" style={{ color, borderBottom: "1px solid #e2e8f0" }}>{title}</h2>
      {children}
    </div>
  );
}
